/**
 * Task Notifications Demo Page
 * Demonstrates the notification and reminder system
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  addNotification,
  deleteNotification,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../store';
import { Notification, Reminder, Task } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SetReminderDialog from '../components/SetReminderDialog';
import NotificationCenter from '../components/NotificationCenter';
import AtRiskTasksDashboard from '../components/AtRiskTasksDashboard';
import TaskHealthBadge from '../components/TaskHealthBadge';
import { Bell, BellDot, Calendar, AlertCircle, TrendingUp } from 'lucide-react';
import { generateUpdateNotifications } from '../services/notificationService';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { useApp } from '../providers/AppProvider';
import { generateId } from '../utils/idGenerator';
import { WIDGET_MUTED_TEXT_CLASS, WIDGET_PANEL_CLASS } from '../constants/widgetTheme';

export default function TaskNotificationsDemo() {
  const { state, setState, loading } = useApp();
  const { tasks, projectIdSet, users } = useOrganizationData(state);
  const currentUser = state?.currentUser;
  const notificationCount = state?.notifications?.length || 0;

  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const hasAttemptedSeedRef = useRef(false);

  useEffect(() => {
    hasAttemptedSeedRef.current = false;
  }, [state?.currentUser?.id]);

  /* ------------------------------------------------------------------ */
  /* INITIALIZE SAMPLE NOTIFICATIONS                                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!currentUser || hasAttemptedSeedRef.current) {
      return;
    }

    if (
      notificationCount > 0 ||
      !users?.length ||
      !tasks?.length
    ) {
      return;
    }

    hasAttemptedSeedRef.current = true;

    const currentUserId = currentUser.id;

    let cancelled = false;

    const seedNotifications = async () => {
      const notifications = generateUpdateNotifications(
        tasks,
        users,
        currentUser.organizationId
      ).filter((notification) => notification.userId === currentUserId);

      if (!notifications.length) {
        return;
      }

      for (const notification of notifications) {
        if (cancelled) {
          return;
        }

        await addNotification(notification);
      }
    };

    seedNotifications().catch((error) => {
      console.error('[TaskNotificationsDemo] Failed to seed notifications:', error);
    });

    return () => {
      cancelled = true;
    };

  }, [tasks, users, currentUser, notificationCount]);


  /* ------------------------------------------------------------------ */
  /* SAVE REMINDER                                                      */
  /* ------------------------------------------------------------------ */
  const handleSaveReminder = async (reminder: Reminder, nextUpdateDate?: string) => {

    if (!state?.currentUser || !state.currentOrganization) return;
    const notification: Notification = {
      id: generateId('notif', reminder.entityType, reminder.entityId, state.currentUser.id),
      organizationId: state.currentOrganization.id,
      userId: state.currentUser.id,
      type: "reminder",
      priority: "medium",
      title: "Reminder Set",
      message: `Reminder set for ${reminder.entityTitle}`,
      entityType: reminder.entityType,
      entityId: reminder.entityId,
      read: false,
      createdAt: new Date().toISOString(),
    };

    await addNotification(notification);

    setState(prev => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        reminders: [...(prev.reminders || []), reminder],
      };
    });

    setShowReminderDialog(false);
    setSelectedTask(null);
  };


  /* ------------------------------------------------------------------ */
  /* NOTIFICATION ACTIONS                                               */
  /* ------------------------------------------------------------------ */
  const handleMarkAsRead = async (notificationId: string) => {
    await markNotificationAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead(state?.currentUser?.id);
  };

  const handleDismissNotification = async (notificationId: string) => {
    await deleteNotification(notificationId);
  };


  /* ------------------------------------------------------------------ */
  /* DERIVED DATA                                                       */
  /* ------------------------------------------------------------------ */
  const unreadCount = useMemo(
    () => (state?.notifications || []).filter(n => !n.read).length,
    [state?.notifications]
  );

  if (loading || !state) {
    return <div className="p-8 text-center">Loading notifications...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Task Notifications & Reminders</h1>
          <p className={`mt-2 ${WIDGET_MUTED_TEXT_CLASS}`}>
            Manage reminders, track updates, and monitor at-risk tasks
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="relative"
            onClick={() => setShowNotificationCenter(true)}
          >
            {unreadCount > 0 ? (
              <>
                <BellDot className="w-5 h-5 mr-2" />
                <span className="absolute -top-1 -right-1 bg-destructive/10 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              </>
            ) : (
              <>
                <Bell className="w-5 h-5 mr-2" />
              </>
            )}
            Notifications
          </Button>
        </div>
      </div>

      <Tabs defaultValue="at-risk" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="at-risk">
            <AlertCircle className="w-4 h-4 mr-2" />
            At Risk Tasks
          </TabsTrigger>
          <TabsTrigger value="all-tasks">
            <TrendingUp className="w-4 h-4 mr-2" />
            All Tasks
          </TabsTrigger>
          <TabsTrigger value="reminders">
            <Bell className="w-4 h-4 mr-2" />
            Reminders ({state.reminders?.length})
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <BellDot className="w-4 h-4 mr-2" />
            Notifications ({state.notifications?.length})
          </TabsTrigger>
        </TabsList>

        {/* At Risk Tasks Tab */}
        <TabsContent value="at-risk" className="space-y-4">
          <AtRiskTasksDashboard
            tasks={tasks}
            users={users}
            projectId={[...projectIdSet]}
          />
        </TabsContent>

        {/* All Tasks Tab */}
        <TabsContent value="all-tasks" className="space-y-4">
          <Card className={WIDGET_PANEL_CLASS}>
            <CardHeader>
              <CardTitle>All Project Tasks with Health Status</CardTitle>
              <CardDescription>
                Click on any task to set reminders and update schedules
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tasks.slice(0, 15).map(task => (
                  <Card
                    key={task.id}
                    className={`${WIDGET_PANEL_CLASS} cursor-pointer transition-shadow hover:shadow-md`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold mb-2">{task.title}</h4>
                          <div className="flex items-center gap-2">
                            <TaskHealthBadge task={task} />
                            {task.targetEndDate && (
                              <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                                Due: {new Date(task.targetEndDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowReminderDialog(true);
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Set Reminder
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <Card className={WIDGET_PANEL_CLASS}>
            <CardHeader>
              <CardTitle>Active Reminders</CardTitle>
              <CardDescription>
                Manage your scheduled reminders and update dates
              </CardDescription>
            </CardHeader>
            <CardContent>
              {state.reminders?.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="mx-auto mb-3 h-12 w-12 text-muted-foreground/60" />
                  <h3 className="mb-1 text-lg font-semibold text-foreground">No reminders set</h3>
                  <p className={`mb-4 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Set reminders to stay on track with your tasks and projects
                  </p>
                  <Button
                    onClick={() => {
                      setSelectedTask(tasks[0]);
                      setShowReminderDialog(true);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Create First Reminder
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {state.reminders?.map(reminder => (
                    <Card key={reminder.id} className={WIDGET_PANEL_CLASS}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{reminder.entityTitle}</h4>
                            <p className={`mt-1 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                              {reminder.reminderType} - {new Date(reminder.reminderDate).toLocaleDateString()}
                            </p>
                            {reminder.nextExpectedUpdateDate && (
                              <p className="mt-1 text-sm text-primary">
                                Next update: {new Date(reminder.nextExpectedUpdateDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <span className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>{reminder.status}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <NotificationCenter
            notifications={state.notifications}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onDismiss={handleDismissNotification}
          />
        </TabsContent>
      </Tabs>

      {/* Reminder Dialog */}
      {showReminderDialog && selectedTask && state.currentUser && (
        <SetReminderDialog
          entity={selectedTask}
          entityType="task"
          currentUserId={state.currentUser.id}
          assigneeIds={selectedTask.assigneeIds}
          onSave={handleSaveReminder}
          onClose={() => {
            setShowReminderDialog(false);
            setSelectedTask(null);
          }}
        />
      )}

      {/* Notification Center Modal */}
      {showNotificationCenter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <NotificationCenter
            notifications={state.notifications}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onDismiss={handleDismissNotification}
            onClose={() => setShowNotificationCenter(false)}
          />
        </div>
      )}
    </div>
  );
}